import type { IntegrationActionControlBoxIOInterface } from '@sb/integrations/io/types';
import { executeControlBoxIOAction } from '@sb/integrations/io/utils/execute/executeControlBoxIOAction';
import type { DeviceCommand, DeviceKind } from '@sb/integrations/types';
import { Device } from '@sb/integrations/types/DeviceInterface';
import * as log from '@sb/log';
import type { DeviceKinematics } from '@sb/motion-planning';
import type { RobotInterface } from '@sb/routine-runner';

import type {
  WeldPositionerConfiguration,
  WeldPositionerSensor,
} from '../types';
import { WeldPositionerCommand, WeldPositionerState } from '../types';

const ns = log.namespace('WeldPositioner');

/**
 * Tighter than `MonitorCNCMachineViaControlBoxIO`'s 100ms because the arc is
 * live while we wait, so latency here is deposited metal. No tighter than
 * ~10ms, which is where `getIO()`'s ~100 Hz snapshot stops changing.
 */
const COMPLETION_POLL_INTERVAL_MS = 20;

/**
 * How an armed completion detector ended. `destroyed` so a weld whose
 * positioner is taken out from under it learns that, rather than awaiting a
 * promise nothing will settle.
 */
export type RotationCompletionOutcome = 'completed' | 'cancelled' | 'destroyed';

export class WeldPositioner extends Device<WeldPositionerState> {
  public constructor(
    protected readonly config: WeldPositionerConfiguration,
    private readonly robot: RobotInterface,
  ) {
    super();

    // Teardown runs `destroy`, not `stop` — `stop` leaves the device in the
    // registry. Hooked through `untilDestroyed` because `destroy`'s signature
    // is generic in the base class's event types.
    const releaseDetectors = () => {
      [...this.activeCompletionPolls].forEach((finish) => finish('destroyed'));
    };

    this.untilDestroyed().then(releaseDetectors, releaseDetectors);
  }

  public readonly kind: DeviceKind = 'WeldPositioner';

  public readonly stateSchema = WeldPositionerState;

  public readonly kinematics: DeviceKinematics<DeviceCommand> = {
    name: 'Weld positioner',
  };

  private isRotating = false;

  /** Exit function per armed detector, so `destroy` can settle them. */
  private readonly activeCompletionPolls = new Set<
    (outcome: RotationCompletionOutcome) => void
  >();

  public async executeCommand(command: DeviceCommand): Promise<void> {
    const parsed = WeldPositionerCommand.safeParse(command);

    if (!parsed.success) {
      throw new Error('Error parsing Weld positioner command');
    }

    const { subCommand } = parsed.data;

    switch (subCommand.kind) {
      case 'rotate':
        await this.assertTrigger();

        break;
      case 'stop':
        await this.releaseTrigger();

        break;
      default: {
        const exhaustive: never = subCommand;
        throw new Error(
          `Unexpected positioner sub-command ${JSON.stringify(exhaustive)}`,
        );
      }
    }
  }

  /**
   * Return the positioner to rest. Called when the robot leaves normal running
   * for any reason, which is the whole reason the positioner is equipment and
   * not a port the step writes to: a raw output is not in the device registry,
   * so nothing would drop it and the part would keep turning under a dead
   * routine.
   *
   * Contract-bound not to throw and to be safe from any state, so the release
   * is unconditional — `isRotating` is our own bookkeeping and a desynced
   * `false` must not be what leaves the trigger asserted.
   */
  public override async resetToSafeState(): Promise<void> {
    try {
      await this.releaseTrigger();
    } catch (error) {
      log.error(
        ns`resetToSafeState`,
        'Failed to release the positioner rotation trigger',
        { error },
      );
    }
  }

  /**
   * Arm a one-shot detector and resolve on the completion sensor's next
   * inactive -> active edge.
   *
   * Caller-scoped rather than latched on the device, so a stray edge — an
   * operator hand-indexing the part during setup — cannot look like a
   * completed weld.
   *
   * Arm before commanding `rotate`: the baseline is read here, which is what
   * tells a sensor that idles asserted (a home index) from one that has just
   * come round.
   *
   * Settles `completed`, `cancelled` or `destroyed`, but never when the signal
   * simply does not arrive — callers need their own timeout.
   */
  public awaitRotationComplete(): {
    completed: Promise<RotationCompletionOutcome>;
    cancel: () => void;
  } {
    let settle!: (outcome: RotationCompletionOutcome) => void;

    const completed = new Promise<RotationCompletionOutcome>((resolve) => {
      settle = resolve;
    });

    // Baseline at rest. `null` (no sensor wired) never becomes a rising edge.
    let previous = this.readSensor(this.config.completionSensor);

    // Declared here so `finish` can clear it, assigned below so `finish` is in
    // scope for the tick that resolves.
    let intervalID: ReturnType<typeof setInterval>;

    /** The one exit. Resolving twice is a no-op, so racing it is harmless. */
    const finish = (outcome: RotationCompletionOutcome) => {
      clearInterval(intervalID);
      this.activeCompletionPolls.delete(finish);
      settle(outcome);
    };

    intervalID = setInterval(() => {
      const isComplete = this.readSensor(this.config.completionSensor);
      const isRisingEdge = isComplete === true && previous === false;

      previous = isComplete;

      if (isRisingEdge) {
        finish('completed');
      }
    }, COMPLETION_POLL_INTERVAL_MS);

    // A weld that is waiting must not be what keeps the process alive.
    intervalID.unref?.();

    this.activeCompletionPolls.add(finish);

    return { completed, cancel: () => finish('cancelled') };
  }

  public initialize(): void {}

  public async stop(): Promise<void> {
    await this.resetToSafeState();
  }

  public update(): WeldPositionerState {
    return {
      kind: 'WeldPositioner',
      name: this.config.name,
      isRotating: this.isRotating,
      isAligned: this.readSensor(this.config.alignmentSensor),
      isComplete: this.readSensor(this.config.completionSensor),
    };
  }

  /**
   * Refuses to report rotation it cannot command. `executeControlBoxIOAction`
   * skips outputs with no port, so without this an unconfigured positioner
   * would write no IO, resolve successfully and still set `isRotating` — and a
   * rotary weld would burn in one spot on a part that never turned. The throw
   * surfaces through the step's `commandPositioner` as an IO failure.
   *
   * Release stays unguarded on purpose: dropping a trigger that was never
   * asserted is a harmless no-op, and `resetToSafeState` must not throw.
   */
  private async assertTrigger(): Promise<void> {
    if (!this.config.rotate.outputs.some(({ ioPort }) => ioPort != null)) {
      throw new Error(
        'Weld positioner has no rotation trigger port configured',
      );
    }

    await executeControlBoxIOAction(this.rotateAction, { robot: this.robot });
    this.isRotating = true;
  }

  private async releaseTrigger(): Promise<void> {
    await executeControlBoxIOAction(this.releaseAction, { robot: this.robot });
    this.isRotating = false;
  }

  /**
   * The rotate action with pulsing forced off. The schema's `isPulse: false`
   * default only applies when `rotate` is absent entirely —
   * `IntegrationActionControlBoxIOOutputs` defaults it to *true* — so a config
   * written by anything but this UI can arrive pulsed, which would release the
   * trigger `pulseSeconds` into the weld and leave the arc burning on a
   * stopped part.
   */
  private get rotateAction(): IntegrationActionControlBoxIOInterface {
    return {
      ...this.config.rotate,
      outputs: this.config.rotate.outputs.map((output) => ({
        ...output,
        isPulse: false,
      })),
    };
  }

  /**
   * The rotate action with every level inverted. `isPulse` is forced off:
   * releasing is a level change by definition, and a pulsed release would
   * re-assert the trigger a moment later — turning a stop into a restart.
   */
  private get releaseAction(): IntegrationActionControlBoxIOInterface {
    return {
      ...this.config.rotate,
      outputs: this.config.rotate.outputs.map((output) => ({
        ...output,
        isHigh: !output.isHigh,
        isPulse: false,
      })),
    };
  }

  /**
   * A role's current read, or null when the cell has no such sensor. Null is
   * "no signal to read", which callers must not read as a sensor that
   * reported false.
   */
  private readSensor(sensor: WeldPositionerSensor | null): boolean | null {
    if (sensor === null) {
      return null;
    }

    return this.robot.getIO()[sensor.ioPort] === sensor.activeLevel;
  }
}
